import type { CatalogComponentProperties } from '@/domain/catalog/common';
import { HeadSystem } from '@/domain/catalog/head';
import { assert } from '@/util/assert';

export enum StemSystem {
    // noinspection JSUnusedGlobalSymbols
    AVENIR_COMPLETE = 'avenir-complete',
    TAPERLOC_COMPLETE = 'taperloc-complete',
    MS30 = 'ms-30',
    Z1 = 'z1',
}

export enum TaperlocStemSize {
    // noinspection JSUnusedGlobalSymbols
    S4 = 4,
    S5 = 5,
    S6 = 6,
    S7 = 7,
    S8 = 8,
    S9 = 9,
    S10 = 10,
    S11 = 11,
    S12 = 12,
    S13 = 13,
    S14 = 14,
    S15 = 15,
    S16 = 16,
    S17 = 17,
    S18 = 18,
    S19 = 19,
    S20 = 20,
    S22 = 22,
    S24 = 24,
}

export enum AvenirStemSize {
    // noinspection JSUnusedGlobalSymbols
    S0 = 0,
    S1 = 1,
    S2 = 2,
    S3 = 3,
    S4 = 4,
    S5 = 5,
    S6 = 6,
    S6_5 = 6.5,
    S7 = 7,
    S7_5 = 7.5,
    S8 = 8,
    S8_5 = 8.5,
    S9 = 9,
}

export enum MS30StemSize {
    // noinspection JSUnusedGlobalSymbols
    S6 = 6,
    S8 = 8,
    S10 = 10,
    S12 = 12,
    S14 = 14,
    S16 = 16,
}

export enum Z1StemSize {
    // noinspection JSUnusedGlobalSymbols
    S0 = 0,
    S1 = 1,
    S2 = 2,
    S3 = 3,
    S4 = 4,
    S5 = 5,
    S6 = 6,
    S7 = 7,
    S8 = 8,
    S9 = 9,
    S10 = 10,
    S11 = 11,
    S12 = 12,
}

export enum StemProfile {
    // noinspection JSUnusedGlobalSymbols
    REDUCED_DISTAL = 'Reduced Distal',
    FULL_PROFILE = 'Full Profile',
    MICROPLASTY = 'Microplasty',
    MICROPLASTY_LEGACY = 'Microplasty Legacy',
    COLLARLESS = 'Collarless',
    COLLARED = 'Collared',
    DEFAULT = 'Default',
    CEMENTLESS_COLLARED = 'Cementless Collared',
    CEMENTED_COLLARLESS = 'Cemented Collarless',
}

export enum StemOffset {
    // noinspection JSUnusedGlobalSymbols
    HIGH_OFFSET = 'High Offset',
    STANDARD_OFFSET = 'Standard Offset',
    XR123 = 'XR123',
    COXA_VARA = 'Coxa Vara',
    LATERAL_OFFSET = 'Lateral Offset',
}

export const stemSystems = Object.values(StemSystem);
export type StemSystemValue = (typeof stemSystems)[number];

export function isStemSystem(value: string): value is StemSystemValue {
    return stemSystems.includes(value as StemSystemValue);
}

const taperlocStemSizes = Object.values(TaperlocStemSize).filter(
    (value): value is number => typeof value === 'number',
);
export type TaperlocStemSizeValue = (typeof taperlocStemSizes)[number];

const avenirStemSizes = Object.values(AvenirStemSize).filter(
    (value): value is number => typeof value === 'number',
);
export type AvenirStemSizeValue = (typeof avenirStemSizes)[number];

const ms30StemSizes = Object.values(MS30StemSize).filter(
    (value): value is number => typeof value === 'number',
);
export type MS30StemSizeValue = (typeof ms30StemSizes)[number];

const z1StemSizes = Object.values(Z1StemSize).filter(
    (value): value is number => typeof value === 'number',
);
export type Z1StemSizeValue = (typeof z1StemSizes)[number];

export const stemSizes = [
    ...taperlocStemSizes,
    ...avenirStemSizes,
    ...ms30StemSizes,
    ...z1StemSizes,
] as const;
export type StemSize =
    | AvenirStemSizeValue
    | TaperlocStemSizeValue
    | MS30StemSizeValue
    | Z1StemSizeValue;
export type StemSizeString = `${StemSize}`;

export function isStemSize(value: number): value is StemSize {
    return stemSizes.includes(value as StemSize);
}

export function isStemSizeString(value: string): value is StemSizeString {
    return isStemSize(parseInt(value));
}

export function stemSizeFromString(value: string): StemSize {
    return Number(value) as StemSize;
}

export const stemProfiles = Object.values(StemProfile);
export type StemProfileValue = (typeof stemProfiles)[number];

export function isStemProfile(value: string): value is StemProfileValue {
    return stemProfiles.includes(value as StemProfileValue);
}

/** Display strings for stem profiles */
const STEM_PROFILE_NAME: { [K in StemProfileValue]: string } = {
    ['Full Profile']: 'Full',
    ['Reduced Distal']: 'Reduced',
    ['Microplasty']: 'Microplasty',
    ['Microplasty Legacy']: 'Microplasty',
    ['Collarless']: 'Collarless',
    ['Collared']: 'Collared',
    ['Default']: '',
    ['Cementless Collared']: 'Cementless Collared',
    ['Cemented Collarless']: 'Cemented Collarless',
};

/** Get the display name for a string profile */
export function formatStemProfile(profile: StemProfileValue): string {
    return STEM_PROFILE_NAME[profile];
}

export function formatStemSystem(system: StemSystemValue): string {
    switch (system) {
        case StemSystem.AVENIR_COMPLETE:
            return 'Avenir';
        case StemSystem.TAPERLOC_COMPLETE:
            return 'Taperloc';
        case StemSystem.MS30:
            return 'MS-30';
        case StemSystem.Z1:
            return 'Z1';
    }
}

/**
 * The head family a stem system pairs with: Taperloc uses TYPE1_COCR; Avenir, MS-30 and Z1 all
 * share the COCR 12/14 family.
 */
export function headSystemForStem(system: StemSystemValue): HeadSystem {
    switch (system) {
        case StemSystem.TAPERLOC_COMPLETE:
            return HeadSystem.TYPE1_COCR;
        case StemSystem.AVENIR_COMPLETE:
        case StemSystem.MS30:
        case StemSystem.Z1:
            return HeadSystem.COCR_1214;
        default:
            // Not a known stem system — reject at the domain boundary rather than silently
            // defaulting to a COCR head family (which would yield wrong head offsets).
            throw new Error(`Unsupported stem system: ${system}`);
    }
}

export const stemOffsets = Object.values(StemOffset);
export type StemOffsetValue = (typeof stemOffsets)[number];

export function isStemOffset(value: string): value is StemOffsetValue {
    return stemOffsets.includes(value as StemOffsetValue);
}

export type StemType = `${StemProfileValue}[${StemOffsetValue}]`;

export function isStemType(value: string): value is StemType {
    const matches = value.match(/(.*)\[(.*)]/);
    return !!matches && isStemProfile(matches[1]) && isStemOffset(matches[2]);
}

export function removeLegacyFromStemType(type: StemType): StemType {
    const matches = type.match(/(.*)\[(.*)]/);
    if (matches) {
        const profile = matches[1];
        const offset = matches[2];
        if (isStemProfile(profile) && isStemOffset(offset)) {
            if (profile != StemProfile.MICROPLASTY_LEGACY) {
                return type;
            }
            return `${StemProfile.MICROPLASTY}[${offset}]`;
        }
    }
    throw Error(`Failed to extract stem-type info from '${type}'`);
}

/** Create a stem-type as a combination of profile and offset */
export function stemType(info: StemTypeInfo): StemType;
export function stemType(profile: StemProfileValue, offset: StemOffsetValue): StemType;
export function stemType(
    arg0: StemProfileValue | StemTypeInfo,
    offset?: StemOffsetValue,
): StemType {
    if (typeof arg0 === 'string') {
        assert(offset);
        return `${arg0}[${offset}]`;
    } else {
        return `${arg0.profile}[${arg0.offset}]`;
    }
}

export type StemTypeInfo = {
    profile: StemProfileValue;
    offset: StemOffsetValue;
};

/** Extract the stem profile and offset from a stem type, which is just a combination of the two */
export function stemTypeInfo(type: StemType): StemTypeInfo {
    const matches = type.match(/(.*)\[(.*)]/);
    if (matches) {
        const profile = matches[1];
        const offset = matches[2];
        if (isStemProfile(profile) && isStemOffset(offset)) {
            return { profile, offset };
        }
    }

    throw Error(`Failed to extract stem-type info from '${type}'`);
}

/** The properties of a type of stem, independent of its use in any particular surgical plan. */
export type CatalogStem = CatalogComponentProperties & {
    /** The stem-system: either Taperloc or Avenir */
    system: StemSystemValue;

    /** The stem-size */
    size: StemSizeString;

    /** The stem-type, a combination of the stem-profile and the stem-offset */
    type: StemType;

    /** The stem-profile: the shape of the main femoral part of the stem */
    profile: StemProfileValue;

    /** The stem-offset: the angle of the neck, and therefore head, relative to the femoral part of the stem */
    offset: StemOffsetValue;
};

/** Create label/name for a stem combining the stem profile, size, and offset e.g. 'Reduced Distal 5 High Offset' */
export const formatStemLabel = (stem: CatalogStem): string => {
    const { profile, offset } = stemTypeInfo(stem.type);
    if (profile === StemProfile.DEFAULT) {
        return `${stem.size} ${offset}`;
    }
    return `${profile} ${stem.size} ${offset}`;
};
